import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowRight, CheckCircle, Clock, Smartphone, Laptop, Tablet, Watch, Lock, ShoppingCart, Plus } from "lucide-react";
import { macServices } from "../data/repairData";
import { useCart } from "../context/CartContext";

const iconMap = { Smartphone, Laptop, Tablet, Watch };

const slugify = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

function AddButton({ item, service, price, deviceType }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  if (typeof price !== "number") return null;

  const handleAdd = () => {
    addItem({
      id: `${deviceType}_${slugify(item.model)}_${service}`,
      name: `${item.model} — ${service === "ecran" ? "Remplacement écran" : service === "batterie" ? "Remplacement batterie" : service === "efi" ? "Déblocage EFI & iCloud" : service === "clavier" ? "Réparation clavier" : service === "vitre_arriere" ? "Vitre arrière" : service === "connecteur" ? "Connecteur de charge" : service}`,
      price,
      type: "repair",
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <button
      onClick={handleAdd}
      title={`Ajouter au panier — ${price}€`}
      className={`ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200 flex-shrink-0 ${
        added
          ? "bg-[#34C759] text-white scale-110"
          : "bg-[#FF5C00]/15 hover:bg-[#FF5C00] text-[#FF5C00] hover:text-white"
      }`}
    >
      {added ? <CheckCircle className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
    </button>
  );
}

function PriceTag({ value }) {
  if (typeof value === "string") {
    return <span className="text-sm text-white/40 italic">{value}</span>;
  }
  return <span className="font-black text-white">{value}€</span>;
}

function MacServiceAddButton({ label, price, idx }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const handleAdd = () => {
    addItem({ id: `macsvc_${idx}`, name: label, price, type: "repair" });
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };
  return (
    <button
      onClick={handleAdd}
      className={`flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 ${
        added ? "bg-[#34C759] text-white" : "bg-[#FF5C00]/10 hover:bg-[#FF5C00] text-[#FF5C00] hover:text-white"
      }`}
      data-testid={`macsvc-add-${idx}`}
    >
      {added ? <CheckCircle className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
      {added ? "Ajouté !" : "Ajouter"}
    </button>
  );
}

export default function RepairPage({ data, repairType }) {
  const [activeGamme, setActiveGamme] = useState(0);
  const IconComp = iconMap[repairType] || Smartphone;
  const { setIsOpen } = useCart();

  const isWatch = repairType === "Watch";
  const isMac = repairType === "Laptop";
  const deviceType = repairType.toLowerCase();

  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-16 max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          <div className="flex-1">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 mb-6 transition-colors">
              <ChevronRight className="w-3 h-3 rotate-180" />
              Retour à l'accueil
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#FF5C00]/10 flex items-center justify-center">
                <IconComp className="w-6 h-6 text-[#FF5C00]" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#FF5C00]">Réparation</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-4">
              {data.title}
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-xl">
              {data.description}
            </p>

            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/devis"
                data-testid="repair-devis-btn"
                className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-6 py-3.5 rounded-full transition-all duration-200 hover:-translate-y-0.5"
              >
                Devis gratuit <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="repair-rdv-btn"
                className="flex items-center gap-2 bg-transparent text-white border border-white/20 hover:border-white/50 font-semibold px-6 py-3.5 rounded-full transition-colors"
              >
                Prendre RDV
              </Link>
              <button
                data-testid="repair-cart-btn"
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 bg-transparent text-white/60 border border-white/10 hover:border-white/30 hover:text-white font-semibold px-6 py-3.5 rounded-full transition-colors"
              >
                <ShoppingCart className="w-4 h-4" /> Mon panier
              </button>
            </div>
          </div>

          {/* Services list */}
          <div className="lg:w-72 bg-[#121215] rounded-2xl border border-white/5 p-6">
            <h3 className="text-sm font-bold text-white mb-4">Réparations disponibles</h3>
            <ul className="space-y-2.5">
              {data.services.map((service) => (
                <li key={service} className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#FF5C00] flex-shrink-0" />
                  <span className="text-sm text-white/70">{service}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-white/5">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#34C759]" />
                <span className="text-sm text-white/60">Diagnostic gratuit avant intervention</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing tables */}
      <section data-testid="pricing-section" className="pb-24 max-w-7xl mx-auto px-6">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-2">Tarifs</p>
          <h2 className="text-3xl font-black tracking-tight text-white">Grille tarifaire</h2>
          <p className="text-sm text-white/40 mt-2">
            * Tarifs indicatifs — un diagnostic est réalisé avant toute intervention.
          </p>
        </div>

        {/* Gamme tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {data.gammes.map((gamme, idx) => (
            <button
              key={gamme.name}
              data-testid={`gamme-tab-${idx}`}
              onClick={() => setActiveGamme(idx)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeGamme === idx
                  ? "bg-[#FF5C00] text-white"
                  : "bg-[#121215] text-white/50 hover:text-white border border-white/10 hover:border-white/20"
              }`}
            >
              {gamme.name}
            </button>
          ))}
        </div>

        {/* Pricing table */}
        {data.gammes[activeGamme] && (
          <div data-testid="pricing-table" className="border border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left table-fixed">
                <thead>
                  <tr className="border-b border-white/10 bg-[#121215]">
                    <th className="py-4 px-4 text-xs font-bold uppercase tracking-widest text-white/30 w-[25%]">Modèle</th>
                    {isMac && (
                      <th className="py-4 px-4 text-xs font-bold uppercase tracking-widest text-white/30 text-center hidden sm:table-cell w-[18.75%]">
                        Déblocage EFI
                      </th>
                    )}
                    <th className="py-4 px-4 text-xs font-bold uppercase tracking-widest text-white/30 text-center w-[18.75%]">Écran</th>
                    <th className="py-4 px-4 text-xs font-bold uppercase tracking-widest text-white/30 text-center w-[18.75%]">Batterie</th>
                    {!isWatch && !isMac && (
                      <th className="py-4 px-4 text-xs font-bold uppercase tracking-widest text-white/30 text-center hidden sm:table-cell w-[18.75%]">
                        Connecteur de charge
                      </th>
                    )}
                    {!isWatch && !isMac && (
                      <th className="py-4 px-4 text-xs font-bold uppercase tracking-widest text-white/30 text-center hidden md:table-cell w-[18.75%]">
                        Vitre arrière
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.gammes[activeGamme].models.map((item, i) => (
                    <tr
                      key={i}
                      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                      data-testid={`pricing-row-${i}`}
                    >
                      <td className="py-4 px-4">
                        <span className="font-semibold text-white text-sm">{item.model}</span>
                      </td>
                      {isMac && (
                        <td className="py-4 px-4 text-center hidden sm:table-cell">
                          <div className="flex items-center justify-center gap-1">
                            <PriceTag value={item.efi} />
                            <AddButton item={item} service="efi" price={item.efi} deviceType={deviceType} />
                          </div>
                        </td>
                      )}
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <PriceTag value={item.ecran} />
                          <AddButton item={item} service="ecran" price={item.ecran} deviceType={deviceType} />
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <PriceTag value={item.batterie} />
                          <AddButton item={item} service="batterie" price={item.batterie} deviceType={deviceType} />
                        </div>
                      </td>
                      {!isWatch && !isMac && (
                        <td className="py-4 px-4 text-center hidden sm:table-cell">
                          <div className="flex items-center justify-center gap-1">
                            <PriceTag value={item.connecteur} />
                            <AddButton item={item} service="connecteur" price={item.connecteur} deviceType={deviceType} />
                          </div>
                        </td>
                      )}
                      {!isWatch && !isMac && (
                        <td className="py-4 px-4 text-center hidden md:table-cell">
                          <div className="flex items-center justify-center gap-1">
                            <PriceTag value={item.vitre_arriere} />
                            <AddButton item={item} service="vitre_arriere" price={item.vitre_arriere} deviceType={deviceType} />
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mac services supplémentaires */}
        {repairType === "Laptop" && (
          <div className="mt-12">
            <h3 className="text-xl font-black text-white mb-2">Autres services Mac</h3>
            <p className="text-sm text-white/40 mb-6">Récupération de données, déblocage EFI & iCloud, virus, SSD…</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {macServices.map((s, idx) => {
                const price = parseFloat(s.price.replace("€", "").replace(",", "."));
                return (
                  <div key={s.label} className="flex items-center gap-4 p-4 rounded-2xl border border-white/5 bg-[#121215] hover:border-white/10 transition-colors">
                    <span className="text-2xl flex-shrink-0">{s.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white leading-snug">{s.label}</p>
                      <p className="text-sm font-black text-[#FF5C00] mt-0.5">{s.price}</p>
                    </div>
                    {!isNaN(price) ? (
                      <MacServiceAddButton label={s.label} price={price} idx={idx} />
                    ) : (
                      s.label.includes("EFI") && <Lock className="w-4 h-4 text-[#FF5C00] flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 rounded-2xl border border-[#FF5C00]/20 bg-[#FF5C00]/5 text-center">
          <h3 className="text-xl font-black text-white mb-2">Votre modèle n'est pas dans la liste ?</h3>
          <p className="text-white/50 text-sm mb-5">Contactez-nous pour un devis personnalisé gratuit.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/devis"
              data-testid="pricing-cta-devis"
              className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-6 py-3 rounded-full transition-colors text-sm"
            >
              Devis en ligne
            </Link>
            <Link
              to="/contact"
              data-testid="pricing-cta-contact"
              className="flex items-center gap-2 border border-white/20 text-white font-semibold px-6 py-3 rounded-full transition-colors hover:border-white/40 text-sm"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
